import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, of } from 'rxjs';
import { distinctUntilChanged, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { AppState } from 'src/store/appstate';
import { selectCompanyByCompanyCode } from 'src/store/entityStores/company-selectors';
import { ActivatedRouteSnapshot } from '@angular/router';
import { getCardNumberFromRoute, getRegionIdFromRoute } from 'src/util/helpers/url-helper';
import { UserService } from '../tcs-identity/user.service';
import { CommCenterAuthorizationService } from './comm-center-authorization.service';
import { ControlZoneAuthorizationService } from './control-zone-authorization.service';
import { RegionAuthorizationService } from './region-authorization.service';
import { CardAuthorizationService } from './card-authorization.service';

@Injectable({ providedIn: 'root' })
export class OpsAuthorizationService {
  constructor(
    private _commCenterAuthz: CommCenterAuthorizationService,
    private _controlZoneAuthz: ControlZoneAuthorizationService,
    private _regionAuthz: RegionAuthorizationService,
    private _cardAuthz: CardAuthorizationService,
    private _user: UserService,
    private _store: Store<AppState>,
  ) {}

  public isAuthorizedForRoute(
    route: ActivatedRouteSnapshot,
    companyCode: string,
  ): Observable<boolean> {
    return this.isAuthorizedForCompanyCode(companyCode).pipe(
      switchMap((isAuthorized) => {
        if (!isAuthorized) {
          return of(false);
        }
        const regionId = getRegionIdFromRoute(route);
        if (regionId != null) {
          return this._regionAuthz.isAuthorizedForCompanyCode(regionId, companyCode);
        }
        return of(true);
      }),
      switchMap((isAuthorized) => {
        if (!isAuthorized) {
          return of(false);
        }
        const cardNumber = getCardNumberFromRoute(route);
        if (cardNumber != null) {
          return this._cardAuthz.isAuthorizedForCardNumber(cardNumber);
        }
        return of(true);
      }),
    );
  }

  public isAuthorizedForCompanyCode(companyCode: string) {
    return this._store
      .select(selectCompanyByCompanyCode(companyCode))
      .pipe(switchMap((c) => (c != null ? this.isAuthorizedForCompanyId(c.id) : of(false))));
  }

  public isAuthorizedForCompanyId(companyId: string) {
    const hasAllCommCenterAccessForCompany$ = this._user.user$.pipe(
      withLatestFrom(this._user.hasAccessibleCompany(companyId)),
      map(([user, hasCompanyAccess]) => {
        if (user == null) {
          return false;
        }
        const allCommCenterAccess = user.allCommunicationCentersAccessible ?? false;
        const hasAccess = allCommCenterAccess && hasCompanyAccess;
        return hasAccess;
      }),
    );

    return combineLatest([
      hasAllCommCenterAccessForCompany$,
      this._controlZoneAuthz.isAuthorizedForCompany(companyId),
      this._commCenterAuthz.isAuthorizedForCompany(companyId),
    ]).pipe(
      map((grants) => {
        const anyGranted = grants.find((hasPermission) => hasPermission) != null;
        return anyGranted;
      }),
      distinctUntilChanged(),
    );
  }
}
